import {
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  getExpandedRowModel,
} from "@tanstack/react-table";
import classNames from "classnames";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import Spinner from "../Spinner";
import { renderDefaultVisible } from "./table.utils";
import TBody from "./TBody";
import THead from "./THead";
import TableHeader from "./TableHeader";
import IndeterminateCheckbox from "./IndeterminateCheckbox";
import { rankItem } from "@tanstack/match-sorter-utils";
import { Link } from "react-router-dom";
import { FolderPlusIcon } from "@heroicons/react/24/outline";
import DropArrow from "./DropArrow";
import { isObject } from "../../utils";
import "./index.css";

/**
 * TanStack Table filterFns must be synchronous + pure.
 * Debouncing belongs in the input layer (see `GlobalFilter`).
 */
function fuzzyFilterFn(row, columnId, value, addMeta) {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta({ itemRank });
  return itemRank.passed;
}

export const Table = forwardRef((props, ref) => {
  const tableData = useMemo(() => props.data || [], [props.data]);
  const tableCols = useMemo(
    () => renderColumns(props),
    [props.columns, props.hideCheckbox]
  );

  const isServerPagination = isObject(props.pagination) || props.manualPagination;

  // Memoize getRowCanExpand to ensure it's stable
  const getRowCanExpand = useMemo(() => {
    if (!props.getRowCanExpand) {
      // If getSubRows is provided but getRowCanExpand is not,
      // default to checking if subRows exist
      if (props.getSubRows) {
        return (row) => {
          const subRows = props.getSubRows(row);
          return subRows && subRows.length > 0;
        };
      }
      return undefined;
    }
    return props.getRowCanExpand;
  }, [props.getRowCanExpand, props.getSubRows]);
  const [columnVisibility, setColumnVisibility] = useState(() =>
    renderDefaultVisible(props.columns)
  );
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: props.pageSize || 20,
  });
  const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper();
  const [expanded, setExpanded] = useState(props.defaultExpanded || {});

  // Memoize table state to prevent unnecessary re-renders
  const tableState = useMemo(
    () => ({
      columnFilters,
      globalFilter,
      columnVisibility,
      rowSelection,
      expanded,
      pagination: isObject(props.pagination)
        ? {
            pageIndex: props.pageIndex,
            pageSize: props.pageSize,
          }
        : pagination,
    }),
    [
      columnFilters,
      globalFilter,
      columnVisibility,
      rowSelection,
      expanded,
      pagination,
      props.pagination,
      props.pageIndex,
      props.pageSize,
    ]
  );

  // Memoize table instance to prevent unnecessary re-creation
  const table = useReactTable({
    data: tableData,
    columns: tableCols,
    state: tableState,
    getRowId: props.getRowId,
    // Server-paginated tables should NOT auto-reset back to page 1 when data changes,
    // otherwise clicking "next page" will fetch page 2 then immediately reset to page 1.
    autoResetPageIndex: isServerPagination ? false : autoResetPageIndex,
    meta: {
      fullTextSearch: props.fullTextSearch,
      skipAutoResetPageIndex,
      ...props.meta,
    },
    filterFns: {
      fuzzy: fuzzyFilterFn,
    },
    initialState: {
      pagination: {
        pageSize: props.pageSize || 20,
      },
    },
    // When server pagination is used, the backend already returns a page-sized dataset.
    // If we also enable TanStack's pagination row model, it will slice again based on
    // pageIndex/pageSize (often resulting in empty rows on page > 0).
    manualPagination: isServerPagination,
    enableRowSelection: true,
    enableSorting: props.enableSorting,
    enableMultiRowSelection: true,
    columnResizeMode: "onChange",
    getRowCanExpand: getRowCanExpand,
    // If expandedLayout is provided, don't use getSubRows (we handle expansion manually)
    // Otherwise, use provided getSubRows or default to row.subRows
    getSubRows: props.expandedLayout
      ? props.getSubRows || undefined
      : props.getSubRows || ((row) => row.subRows), // Support subRows for expandable rows
    onPaginationChange: props.onPaginationChange || setPagination,
    onExpandedChange: setExpanded,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: "fuzzy",
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Only use TanStack's pagination row slicing for purely client-side pagination.
    getPaginationRowModel: isServerPagination ? undefined : getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  /**
   * IMPORTANT:
   * Historically some callers pass `className="w-full"` just to control width.
   * If we treat `className` as a full replacement, we drop critical layout
   * classes (`flex`, `min-h-0`, `h-full`) and the table viewport can collapse
   * in nested flex layouts (this looks “hit or miss”).
   *
   * Default behavior: merge `className` on top of base wrapper classes.
   * Escape hatch: set `replaceWrapperClassName` to keep legacy "replace" behavior.
   */
  const wrapperClasses = props.className
    ? props.className
    : classNames(
        "flex flex-col h-full rounded-lg border border-t-0 border-b-0 bg-gray-50 shadow shadow-t-0",
        props.wrapperClasses,
        {
          "border-t-1": props.hideHeader,
        }
      );
  const classes = props.tableWrapperClasses
    ? props.tableWrapperClasses
    : classNames(
        props.innerWrapClass,
        // NOTE:
        // `inline-block` + `h-full` can fail to stretch in flex/height-constrained
        // layouts (common in dashboard pages), which leads to “table not filling
        // full height” + weird extra scroll behavior.
        "block w-full h-full min-h-0 overflow-x-auto overflow-y-auto",
        "align-middle border-gray-200 rounded-b-lg",
        {
          "border-t": props.title || props.actions,
        }
      );

  const loading = (
    <div
      style={props.tableInnerWrapStyle}
      className="flex items-center justify-center h-full p-3 "
    >
      <Spinner size="md" /> <div className="pl-2">Loading...</div>
    </div>
  );

  const emptyState = React.isValidElement(props.emptyState) ? (
    props.emptyState
  ) : (
    <div className="py-10 h-full ">
      <div className="text-center">
        <FolderPlusIcon className="w-12 h-12 mx-auto text-gray-400 stroke-1" />
        <h3 className="mt-2 font-semibold text-sm">
          {props.emptyState?.title || `No ${props.id || props.title || "Data"}`}
        </h3>
        <p className="text-sm text-gray-500">
          {props.emptyState?.description || "Get started by adding some data"}
        </p>
        {/* <div className="mt-3">{renderButton("md")}</div> */}
      </div>
    </div>
  );

  useEffect(() => {
    // if (table.getState().columnFilters[0]?.id === "fullName") {
    //   if (table.getState().sorting[0]?.id !== "fullName") {
    //     table.setSorting([{ id: "fullName", desc: false }]);
    //   }
    // }
  }, [table.getState().columnFilters[0]?.id]);

  // Optimize selected rows calculation
  const selectedRows = useMemo(
    () => table.getSelectedRowModel().rows.map((item) => item.original),
    [table.getState().rowSelection]
  );

  useEffect(() => {
    if (props.onSelectedRowsChange && selectedRows) {
      props.onSelectedRowsChange(selectedRows);
    }
  }, [selectedRows, props.onSelectedRowsChange]);

  useEffect(() => {
    if (props.pageSize) {
      table.setPageSize(props.pageSize);
    }
  }, [props.pageSize]);

  useImperativeHandle(
    ref,
    () => ({
      resetSelectedRows: (bool) => table.toggleAllRowsSelected(bool),
      resetExpandedRows: (bool) => table.toggleAllRowsExpanded(bool),
      cleanup: () => {
        setColumnFilters([]);
        setGlobalFilter("");
        setRowSelection({});
        setExpanded({});
      },
    }),
    [table]
  );

  const useStyles = !props.turnOffStyles;

  const TableWrapper = props.useDivs || !useStyles ? "div" : "table";

  return (
    <div className={wrapperClasses}>
      {!props.hideHeader &&
        (props.title || props.action || props.search || props.renderHeader) && (
          <TableHeader
            {...props}
            table={table}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        )}
      <div style={props.tableOuterWrapStyle}>
        <div style={props.tableInnerWrapStyle} className={classes}>
          {props.loading ? (
            loading
          ) : !props.data || props.data.length === 0 ? (
            emptyState
          ) : (
            <TableWrapper
              className={props.tableClassName || "min-w-full h-full"}
              style={
                props.useTableSizes || !useStyles
                  ? { width: table.getCenterTotalSize() }
                  : props.wrapperStyles
              }
            >
              <THead
                {...props}
                useStyles={useStyles}
                table={table}
                stickyHeaders={props.stickyHeaders}
              />
              <TBody
                {...props}
                useStyles={useStyles}
                table={table}
                updateData={props.updateData}
                RowComponent={props.RowComponent}
              />
            </TableWrapper>
          )}
        </div>
      </div>
      {(!props.hideFooter &&
        (props.footer ? props.footer : tableData.length > props.pageSize)) ||
      props.showFooter ? (
        <div className="py-3 text-center border-t">
          <Link
            className="text-primary-600 hover:underline"
            to={props.footerLink || `/${props.id}`}
          >
            View All
          </Link>
        </div>
      ) : null}
    </div>
  );
});

export default Table;

export function useSkipper() {
  const shouldSkipRef = React.useRef(true);
  const shouldSkip = shouldSkipRef.current;

  // Wrap a function with this to skip a pagination reset temporarily
  const skip = React.useCallback(() => {
    shouldSkipRef.current = false;
  }, []);

  React.useEffect(() => {
    shouldSkipRef.current = true;
  });

  return [shouldSkip, skip];
}

export function renderColumns(props) {
  const columns = Array.isArray(props.columns) ? props.columns.slice() : [];
  const firstCol = columns[0];
  if (!firstCol) return columns;

  const globalSearchFilters = Array.isArray(props.globalSearchFilters)
    ? props.globalSearchFilters
    : null;

  const normalizeEnableGlobalFilter = (colDef) => {
    // Normalize legacy key names without breaking existing callers
    // (Many configs still use v7-style Header/Cell/accessor)
    const accessorKey =
      colDef?.accessorKey || colDef?.accessor || colDef?.id;
    const id = colDef?.id || accessorKey;
    const header = colDef?.header ?? colDef?.Header;
    const cell = colDef?.cell ?? colDef?.Cell;

    const normalized =
      colDef && typeof colDef === "object"
        ? {
            ...colDef,
            ...(accessorKey ? { accessorKey } : {}),
            ...(id ? { id } : {}),
            ...(header !== undefined ? { header } : {}),
            ...(cell !== undefined ? { cell } : {}),
          }
        : colDef;

    // Always disable global filtering for the selection column
    const colId = normalized?.id ?? normalized?.accessorKey;
    if (colId === "select") return { ...normalized, enableGlobalFilter: false };

    // If a whitelist is provided, only allow those columns to participate
    if (globalSearchFilters && colId) {
      return {
        ...normalized,
        enableGlobalFilter: globalSearchFilters.includes(colId),
      };
    }

    return normalized;
  };

  const normalizedColumns = columns.map(normalizeEnableGlobalFilter);

  // If checkbox selection is enabled, prepend selection column (without mutating caller array)
  if (!props.hideCheckbox) {
    const hasSelect = normalizedColumns.some((c) => c?.id === "select");
    if (!hasSelect) {
      return [
        {
          id: "select",
          enableSorting: false,
          enableHiding: false,
          enableGlobalFilter: false,
          size: 44,
          header: ({ table }) => (
            <IndeterminateCheckbox
              header={true}
              checked={table.getIsAllRowsSelected()}
              indeterminate={table.getIsSomeRowsSelected()}
              onChange={table.getToggleAllPageRowsSelectedHandler()}
              // onChange={table.getToggleAllRowsSelectedHandler()}
            />
          ),
          cell: ({ row }) => (
            <div className="px-1">
              <IndeterminateCheckbox
                checked={row.getIsSelected()}
                indeterminate={row.getIsSomeSelected()}
                onChange={row.getToggleSelectedHandler()}
              />
            </div>
          ),
        },
        ...normalizedColumns,
      ];
    }
    return normalizedColumns;
  }

  // If checkbox selection is hidden, wrap the first column cell to support row expansion UI
  const wrappedFirstCol = {
    ...normalizedColumns[0],
    cell: (cellProps) => {
      const originalCell = normalizedColumns[0]?.cell;
      const rendered =
        typeof originalCell === "function"
          ? originalCell(cellProps)
          : cellProps.getValue?.();

      return cellProps.row.getCanExpand() ? (
        <div
          className="flex items-center"
          style={{
            paddingLeft: `${cellProps.row.depth * 2}rem`,
          }}
        >
          <DropArrow {...cellProps} />
          {rendered}
        </div>
      ) : (
        rendered
      );
    },
  };

  return [wrappedFirstCol, ...normalizedColumns.slice(1)];
}
